import {
  Box,
  Button,
  Paper,
  Typography,
  TextField,
  InputAdornment,
} from "@mui/material";
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { GridRowModes, DataGrid, GridActionsCellItem, GridEditInputCell } from "@mui/x-data-grid";
import EditIcon from "@mui/icons-material/Edit";
import EditNoteIcon from "@mui/icons-material/EditNote";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Close";
import toast, { Toaster } from "react-hot-toast";
import Swal from "sweetalert2";
import * as services from "./../../services/saleng";
import AppBreadcrumbs from "./../../components/AppBreadcrumbs";
import ProductFormDialog from "./ProductFormDialog";
import SearchIcon from "@mui/icons-material/Search";
import { formatDate } from "../../utils/formatDate";
import { formatNumber } from "../../utils/formatNumber";
import SnapshotDialog from "./SnapshotDialog";

const formatUpdatedBy = (user, fallback) => {
  if (!user) return fallback || "-";

  const fullName = [user.name, user.lastname].filter(Boolean).join(" ").trim();
  return fullName || user.username || fallback || "-";
};

const Products = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editData, setEditData] = useState(null);

  const [searchText, setSearchText] = useState("");

  const [openDialog, setOpenDialog] = useState(false);
  const [openSnapshotDialog, setOpenSnapshotDialog] = useState(false);

  const filtered = rows.filter(
    (p) =>
      p.title.toLowerCase().includes(searchText.toLowerCase()) ||
      p.category.includes(searchText) ||
      p.subCategory.includes(searchText)
  );

  const columns = [
    // { field: "no", headerName: "ลำดับ", flex: 0, width: 70 },
    // {
    //   field: "image",
    //   headerName: "รูปภาพ",
    //   flex: 0,
    //   width: 100,
    //   renderCell: (params) =>
    //     params.value && (
    //       <img
    //         src={params.value}
    //         style={{ width: "50px", height: "50px" }}
    //         alt=""
    //       />
    //     ),
    // },
    { field: "code", headerName: "รหัสสินค้า", flex: 0 },
    { field: "title", headerName: "ชื่อสินค้า", flex: 2 },
    { field: "category", headerName: "หมวดหมู่", flex: 1.5 },
    { field: "subCategory", headerName: "หมวดหมู่ย่อย", flex: 1.5 },
    { field: "description", headerName: "คำอธิบาย", flex: 1.5 },
    {
      field: "price",
      headerName: "ราคาสินค้า",
      type: "number",
      editable: true,
      flex: 1.5,
      minWidth: 120,
      align: "right",
      headerAlign: "right",
      renderEditCell: (params) => (
        <GridEditInputCell
          {...params}
          inputProps={{
            ...params.inputProps,
            onFocus: (event) => {
              const target = event.target;
              // Check if the input value has changed (user typed to start edit)
              // If changed, don't select all (let user continue typing)
              // If not changed (clicked to edit), select all
              const originalValue = params.formattedValue ?? params.value ?? "";
              setTimeout(() => {
                if (target.value === String(originalValue)) {
                  target.select();
                }
              }, 0);
            },
          }}
        />
      ),
      renderCell: (params) => {
        const value = params.value ?? 0;
        return (
          <Box
            display={"flex"}
            justifyContent={"flex-end"}
            alignItems={"center"}
            width={"100%"}
            gap={1}
          >
            {formatNumber(value)}
            <EditIcon fontSize="small" color="primary" />
          </Box>
        );
      },
    },
    {
      field: "actions",
      headerName: "",
      flex: 0,
      headerAlign: "center",
      align: "center",
      renderCell: ({ id }) => {
        return [
          <GridActionsCellItem
            key={`${id}-edit`}
            icon={<EditNoteIcon />}
            label="Edit"
            color="primary"
            onClick={handleEditClick(id)}
          />,
          <GridActionsCellItem
            key={`${id}-delete`}
            icon={<DeleteIcon />}
            label="Delete"
            color="error"
            onClick={handleDeleteClick(id)}
          />,
        ];
      },
    },
    {
      field: "updatedAt",
      headerName: "แก้ไขล่าสุด",
      flex: 2,
      renderCell: (params) => formatDate(params.value),
    },
    // {
    //   field: "updatedByName",
    //   headerName: "แก้ไขโดย",
    //   flex: 1.5,
    // },
  ];

  const handleEditClick = (id) => () => {
    const editRow = rows.find((row) => row.id === id);
    setEditData(null);
    setOpenDialog(true);
    setTimeout(() => {
      setEditData(editRow);
    }, 0);
  };



  const handleDeleteClick = (id) => () => {
    const deletedRow = rows.find((row) => row.id === id);

    console.log("deletedRow", deletedRow);
    Swal.fire({
      customClass: { container: "my-swal" },
      title: "ยืนยันที่จะลบ?",
      text: "คุณจะไม่สามารถย้อนกลับสิ่งนี้ได้!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "ยืนยัน",
      cancelButtonText: "ยกเลิก",
    }).then((result) => {
      if (result.isConfirmed) {
        services
          .deleteProduct(deletedRow.id)
          .then((response) => {
            toast.success("ลบสำเร็จ!");
            Swal.fire({
              customClass: { container: "my-swal" },
              title: "ลบแล้ว!",
              text: `${deletedRow.title} ถูกลบแล้ว`,
              icon: "success",
              confirmButtonText: "ตกลง",
            });
            setRows((prevRows) =>
              prevRows.filter((row) => row.id !== deletedRow.id)
            );
          })
          .catch((error) => {
            toast.error("ลบไม่สำเร็จ!");
            console.error("Error deleting product:", error);
          });
      }
    });
  };



  const processRowUpdate = async (newRow) => {
    const price = Number(newRow.price);
    const updatedRow = { ...newRow, price: price < 0 ? 0 : price, isNew: false };
    const payload = { ...newRow, price: price < 0 ? 0 : price };

    try {
      const response = await services.updateProduct(payload, newRow.id);
      const updatedProduct = response?.data?.data;
      const nextRow = updatedProduct
        ? {
            ...updatedRow,
            ...updatedProduct,
            category: updatedRow.category,
            subCategory: updatedRow.subCategory,
            updatedByName: formatUpdatedBy(
              updatedProduct.updatedBy,
              updatedProduct.updatedByUsername
            ),
          }
        : updatedRow;

      toast.success("แก้ไขสำเร็จ!");
      setRows((prevRows) =>
        prevRows.map((row) => (row.id === newRow.id ? nextRow : row))
      );
      return nextRow;
    } catch (error) {
      toast.error("แก้ไขไม่สำเร็จ!");
      console.error("Error updating product:", error);
      throw error; // Revert change in DataGrid
    }
  };

  const fetchProducts = useCallback(async () => {
    const res = await services.getProducts();
    const rawData = res.data.data;

    console.log("products", rawData);

    const productsWithIndex = rawData.map((item, index) => ({
      ...item,
      no: index + 1,
      category: item.category?.name || "",
      subCategory: item.subCategory?.name || "",
      updatedByName: formatUpdatedBy(item.updatedBy, item.updatedByUsername),
    }));

    setRows(productsWithIndex);
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const handleSnapshot = async () => {
    const result = await Swal.fire({
      customClass: { container: "my-swal" },
      title: "ยืนยันการบันทึก?",
      text: "คุณต้องการบันทึก ราคาประกาศ ใช่หรือไม่?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "ยืนยัน",
      cancelButtonText: "ยกเลิก",
    });

    if (result.isConfirmed) {
      try {
        setLoading(true);
        await services.postSnapshot();
        toast.success("บันทึก ราคาประกาศ สำเร็จ!");
        Swal.fire({
          customClass: { container: "my-swal" },
          title: "สำเร็จ!",
          text: "บันทึก ราคาประกาศ เรียบร้อยแล้ว",
          icon: "success",
          confirmButtonText: "ตกลง",
        });
      } catch (error) {
        toast.error("บันทึก ราคาประกาศ ไม่สำเร็จ!");
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <>
      <AppBreadcrumbs />
      <Toaster position="bottom-left" reverseOrder={false} />
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          flexWrap: "wrap",
          gap: 2,
          my: 2,
        }}
      >
        {/* ส่วนซ้าย: หัวข้อ + Search */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "stretch", sm: "center" },
            gap: 2,
            flex: 1,
            minWidth: 0,
          }}
        >
          <Typography component="h2" variant="h5" sx={{ whiteSpace: "nowrap" }}>
            รายการสินค้า
          </Typography>
          <TextField
            fullWidth
            label="ค้นหา"
            placeholder="ชื่อสินค้า / หมวดหมู่"
            variant="outlined"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            sx={{ maxWidth: { xs: "100%", sm: 420 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {/* ปุ่มด้านขวา */}
        <Box display="flex" gap={1}>
          <Button
            color="primary"
            variant="contained"
            onClick={() => setOpenSnapshotDialog(true)}
            sx={{ whiteSpace: "nowrap" }}
          >
            ดูบันทึกราคาประกาศ
          </Button>

          <Button
            color="primary"
            variant="contained"
            onClick={handleSnapshot}
            sx={{ whiteSpace: "nowrap" }}
          >
            บันทึกราคาประกาศ
          </Button>

          <Button
            color="success"
            variant="contained"
            onClick={() => {
              navigate("/step-price");
            }}
            sx={{ whiteSpace: "nowrap" }}
          >
            ราคาขั้นบันไดแบบหมวดหมู่
          </Button>
          <Button
            color="success"
            variant="contained"
            onClick={() => {
              setEditData(null);
              setOpenDialog(true);
            }}
            sx={{ whiteSpace: "nowrap" }}
          >
            เพิ่มรายการสินค้า
          </Button>
        </Box>
      </Box>

      <Box sx={{ flex: 1, position: "relative" }}>
        <Box sx={{ position: "absolute", inset: 0 }}>
          <Paper>
            <DataGrid
              rows={filtered
                .slice() // สร้าง copy ของ array ป้องกัน mutate
                .sort((a, b) => a.code.localeCompare(b.code)) // เรียง code จากน้อยไปมาก
                .map((p, idx) => ({
                  id: p.id,
                  no: idx + 1,
                  ...p,
                }))}
              columns={columns}
              // editMode="cell" is default, removed explicit row mode
              processRowUpdate={processRowUpdate}
              loading={loading}
            // hideFooter={true}          // ซ่อน footer
            // pagination={false}         // ปิด pagination

            />
          </Paper>
        </Box>
      </Box>

      <ProductFormDialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        onSuccess={() => {
          setLoading(true);
          fetchProducts();
        }}
        initialData={editData}
      />

      <SnapshotDialog
        open={openSnapshotDialog}
        onClose={() => setOpenSnapshotDialog(false)}
      />
    </>
  );
};

export default Products;
